import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { Badge } from "@/components/ui/badge";
import { isHazardousEER } from "@/lib/utils";

const LOOKUPS = [
  { name: "codici-eer", label: "Codici EER (CER)", keyFields: ["codice", "descrizione"] },
  { name: "adr-classi", label: "Classi ADR", keyFields: ["codice", "descrizione"] },
  { name: "attivita", label: "Attività R/D", keyFields: ["codice", "descrizione"] },
  { name: "attivita-rs", label: "Attività RS", keyFields: ["codice", "descrizione"] },
  { name: "caratteristiche-pericolo", label: "Caratteristiche di pericolo (HP)", keyFields: ["codice", "descrizione"] },
  { name: "categorie-raee", label: "Categorie RAEE", keyFields: ["codice", "descrizione"] },
  { name: "causali-operazione", label: "Causali operazione", keyFields: ["codice", "descrizione"] },
];

export function Codifiche() {
  const [active, setActive] = useState(LOOKUPS[0].name);
  const [filter, setFilter] = useState("");

  const q = useQuery({
    queryKey: ["lookup", active],
    queryFn: () => endpoints.codifiche.lookup(active),
  });

  const meta = LOOKUPS.find((l) => l.name === active)!;
  const items = (q.data ?? []) as Record<string, unknown>[];
  const filtered = filter
    ? items.filter((it) => JSON.stringify(it).toLowerCase().includes(filter.toLowerCase()))
    : items;

  // Mostra solo le colonne scalari (skippa oggetti/array dalla tabella)
  const cols = items[0]
    ? Object.keys(items[0]).filter((k) => {
        const v = (items[0] as Record<string, unknown>)[k];
        return typeof v !== "object" || v === null;
      }).slice(0, 6)
    : meta.keyFields;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Codifiche RENTRI</h1>
        <p className="text-sm text-muted-foreground">Tabelle ufficiali (cached lato backend)</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {LOOKUPS.map((l) => (
          <Button
            key={l.name}
            size="sm"
            variant={active === l.name ? "default" : "outline"}
            onClick={() => setActive(l.name)}
          >
            {l.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">{meta.label}</CardTitle>
            <Input placeholder="Filtra…" className="w-64" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          {q.isLoading && <CenteredLoader />}
          {q.error && <ErrorState error={q.error} />}
          {!q.isLoading && filtered.length === 0 && <EmptyState title="Nessun elemento" />}
          {filtered.length > 0 && (
            <Table>
              <THead>
                <TR>{cols.map((c) => <TH key={c}>{c}</TH>)}</TR>
              </THead>
              <TBody>
                {filtered.slice(0, 1000).map((row, i) => {
                  const props = (row.properties ?? {}) as Record<string, unknown>;
                  const isPericoloso = props.flag_pericoloso === true;
                  return (
                    <TR key={i}>
                      {cols.map((c) => {
                        const v = String(row[c] ?? "—");
                        const eer = active === "codici-eer" && (c === "code" || c === "codice");
                        return (
                          <TD key={c} className={eer ? "font-mono text-xs" : ""}>
                            {eer ? (
                              <span className="flex items-center gap-2">{v}{(isPericoloso || isHazardousEER(v)) && <Badge variant="destructive" className="text-[10px]">P</Badge>}</span>
                            ) : v}
                          </TD>
                        );
                      })}
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
          {filtered.length > 1000 && (
            <p className="mt-2 text-xs text-muted-foreground">Mostrati 1000 di {filtered.length}. Affina il filtro.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
